/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useRef, useState } from 'react'
import { Box, Divider, Stack } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type EmailLanguage, type SlotEmailSettings, UserType } from '../../models/SlotEmailSettings'
import { useFieldArray, useForm, type SubmitHandler, FormProvider } from 'react-hook-form'
import { notification } from '../../components/Notifications'
import EmailSettingsCard from './components/EmailSettingsCard'
import MergedSurveyNotificationsCard from './components/MergedSurveyNotificationsCard'
import Tab from '@mui/material/Tab'
import TabPanel from '@mui/lab/TabPanel'
import TabList from '@mui/lab/TabList'
import TabContext from '@mui/lab/TabContext'
import DefaultModalActions from '../../components/GenericModal/DefaultModalActions'
import { SlotStatus, type ISlot } from '../../models/DistributionPlan'
import GenericModal from '../../components/GenericModal'
import { ButtonSize, ButtonType } from '../../components/GenericButton/styles'
import Dropdown from '../../components/Dropdown'
import { AutoImportManager } from '../../APIManager'
import { useSearchParams } from 'react-router-dom'
import { useUserRole } from '../../hooks/useUserRole'
import { env } from '../../env'

export const EMAIL_TYPE_TO_TASK: Record<string, string> = {
  STUDENT_EMAIL_REMINDER: 'student_email_task',
  INSTRUCTOR_EMAIL_OPENING: 'instructor_survey_reminder_email_task',
  INSTRUCTOR_EMAIL_CLOSING: 'instructor_survey_reminder_email_task',
  INSTRUCTOR_EMAIL_REMINDER: 'instructor_survey_reminder_email_task',
  INSTRUCTOR_EMAIL_RELEASED: 'instructor_survey_releasing_email_task',
  ADMIN_EMAIL_OPENING: 'admin_distribution_opening_email_task',
  ADMIN_EMAIL_RELEASED: 'admin_distribution_closing_email_task'
}

export interface SlotEmailSettingsFeatureProps {
  handleCloseModal?: () => void
  slot: ISlot
  isOpen: boolean
}

enum tabOptions {
  STUDENT = '1',
  INSTRUCTOR = '2',
  ADMIN = '3'
}

const SUPPORTED_LANGUAGES = [
  { code: 'EN', label: 'English' },
  { code: 'ES', label: 'Español' },
  { code: 'PT', label: 'Português' }
] as const

type LanguageCode = typeof SUPPORTED_LANGUAGES[number]['code']

const SlotEmailSettingsFeature: React.FC<SlotEmailSettingsFeatureProps> = ({
  slot,
  handleCloseModal = () => { },
  isOpen
}): JSX.Element => {
  const slotId = slot.slotId
  const { isGlobalRole } = useUserRole()
  const showDevTools = isGlobalRole && env.REACT_APP_AWS_ENVIRONMENT !== 'prod-classranked'
  const startTab = slot.isSlotAnonymous ? tabOptions.INSTRUCTOR : tabOptions.STUDENT
  const [tab, setTab] = useState<string>(startTab)
  const storedLanguage = slot.slotEmailSettings?.find(s => s.language != null)?.language as LanguageCode | undefined
  // language default = EN
  const [language, setLanguage] = useState<LanguageCode>(storedLanguage ?? 'EN')
  const initialLanguage = useRef<LanguageCode>(storedLanguage ?? 'EN')
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const slotStatus = slot.slotNodeDetails?.slotStatus
  const isSlotClosed = slotStatus === SlotStatus.Closed
  const isSlotPublishedOrLater = slotStatus !== SlotStatus.Draft

  const projectId: string = searchParams.get('projectId') || ''
  const methods = useForm<FormData>({
    defaultValues: {
      settings: slot.slotEmailSettings
    }
  })

  const { handleSubmit, control } = methods
  interface FormData {
    settings?: SlotEmailSettings[]
  }

  const { fields, replace } = useFieldArray({
    control,
    name: 'settings'
  })

  const { mutateAsync, isPending: isUpdating } = useMutation({
    mutationFn: async (data: SlotEmailSettings[]): Promise<any> => {
      const result = await AutoImportManager.patchEmailSettings(slotId, data)
      return result.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['slotDataById', slotId] })
    }
  })

  const onSubmit: SubmitHandler<FormData> = async (data, e) => {
    try {
      if (data.settings) {
        // set language for all settings if it has been changed, otherwise no change
        const languageChanged = language !== initialLanguage.current
        const settings = (languageChanged
          ? data.settings.map(s => ({ ...s, language: language as EmailLanguage }))
          : data.settings
        ).map(({ nextEmailPushTimestamp: _next, lastEmailSentTimestamp: _last, ...rest }) => rest)
        await mutateAsync(settings)
        notification('Successfully updated email settings!', { variant: 'success' })
        handleCloseModal()
        methods.reset()
      }
    } catch (error: any) {
      const detail: string | undefined = error?.response?.data?.detail
      notification(detail ?? 'Failed to update email settings.', { variant: 'error' })
    }
    e?.preventDefault()
  }

  const { data: latestEmailSettings, isFetching: isFetchingLatestEmailSettings, isError: isErrorLatestEmailSettings } = useQuery<SlotEmailSettings[]>({
    queryKey: ['latest_slot_email_settings', projectId],
    retry: false,
    meta: { silent: true },
    queryFn: async (): Promise<SlotEmailSettings[]> => {
      const res = await AutoImportManager.getLatestSlotEmailSettings(projectId)
      return res
    },
    enabled: !!projectId && projectId.trim().length > 0 && !isSlotClosed
  })

  const hasLatestEmailSettings = (latestEmailSettings?.length ?? 0) > 0

  const useLatestEmailSettingsClicked = (): void => {
    if (isFetchingLatestEmailSettings || !hasLatestEmailSettings) {
      return
    }
    try {
      const currentSettingsMap = new Map(
        (methods.getValues('settings') ?? []).map((s) => [s.emailType, s.slotEmailSettingsId])
      )
      let updatedSettingsData = latestEmailSettings?.map((setting) => ({
        ...setting,
        slotEmailSettingsId: currentSettingsMap.get(setting.emailType) ?? '',
        slotId
      }))
      if (slot.isSlotAnonymous) {
        updatedSettingsData = updatedSettingsData?.filter((setting) => setting.userType !== UserType.STUDENT)
      }
      replace(updatedSettingsData ?? [])
      const prevLanguage = latestEmailSettings?.[0]?.language as LanguageCode | undefined
      setLanguage(prevLanguage ?? 'EN')
      notification('Latest email settings have been applied successfully', { variant: 'success' })
    } catch (error) {
      console.error('Error fetching latest email settings', error)
      notification('Failed to fetch latest email settings', { variant: 'error' })
    }
  }

  const handleNextClicked = async (): Promise<void> => {
    if (tab === tabOptions.ADMIN) {
      await handleSubmit(onSubmit)()
      setTab(startTab)
    } else {
      setTab((prevTab) => {
        const nextTab = (parseInt(prevTab) + 1).toString()
        return nextTab
      })
    }
  }

  const handleBackClicked = (): void => {
    if (tab === startTab) {
      handleCloseModal()
      methods.reset()
    } else {
      setTab((prevTab) => {
        const prevTabNumber = parseInt(prevTab) - 1
        return prevTabNumber >= parseInt(startTab) ? prevTabNumber.toString() : startTab
      })
    }
  }

  return (
    <>
      <GenericModal
        isOpen={isOpen}
        title={
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>Survey Project Email Settings</span>
            <Dropdown
              label={language}
              buttonType={ButtonType.Secondary}
              buttonSize={ButtonSize.Medium}
              menuItems={SUPPORTED_LANGUAGES.map(({ code, label }) => ({
                text: label,
                action: () => { setLanguage(code) }
              }))}
              menuPaperStyles={{ minWidth: 'auto' }}
            />
          </Box>
        }
        onClose={handleBackClicked}
        maxWidth='sm'
        showCloseIcon={false}
        action={
          <DefaultModalActions
            destructiveButtonProps={{
              onClick: handleBackClicked,
              text: tab !== startTab ? 'Back' : 'Cancel',
              type: 'button'
            }}
            confirmButtonProps={{
              text: tab !== tabOptions.ADMIN ? 'Next' : 'Save',
              type: 'submit',
              loading: isUpdating,
              disabled: isSlotClosed,
              onClick: handleNextClicked
            }}
            prevSettingsProps={{
              text: isFetchingLatestEmailSettings ? 'Previous Email Settings is Loading...' : 'Apply Previous Email Settings',
              buttonType: ButtonType.Ghost,
              onClick: useLatestEmailSettingsClicked,
              type: 'button',
              // loading: isFetchingLatestEmailSettings,
              disabled: ((!isFetchingLatestEmailSettings && !hasLatestEmailSettings) || isErrorLatestEmailSettings || isSlotClosed)
            }}
          />
        }
      >
        {/* {error && <Alert severity="error">an issue occurred</Alert>} */}
        <FormProvider {...methods}>
          <form>
            <TabContext value={tab}>
              <TabList
                onChange={(e, v: string) => {
                  if (slot.isSlotAnonymous && v === tabOptions.STUDENT) {
                    setTab(tabOptions.INSTRUCTOR)
                  } else {
                    setTab(v)
                  }
                }}
              >
                {!slot.isSlotAnonymous && <Tab label="Student" value={tabOptions.STUDENT} />}
                <Tab label="Instructor" value={tabOptions.INSTRUCTOR} />
                <Tab label="Admin" value={tabOptions.ADMIN} />
              </TabList>

              {!slot.isSlotAnonymous && (
                <TabPanel value={tabOptions.STUDENT} sx={{ paddingTop: 4 }}>
                  <Stack>
                    {fields?.map((field, index) => {
                      if (field.userType !== 'STUDENT') {
                        return <></>
                      }
                      return <EmailSettingsCard isReadOnly={isSlotClosed} isSlotPublishedOrLater={isSlotPublishedOrLater} key={field.id} name={`settings.${index}`} setting={field} control={control} showDevTools={showDevTools} devTaskName={EMAIL_TYPE_TO_TASK[field.emailType]} slotId={slotId} />
                    })}
                  </Stack>
                </TabPanel>
              )}
              <TabPanel value={tabOptions.INSTRUCTOR} sx={{ paddingTop: 4 }}>
                <Stack>
                  {(() => {
                    const instructorFields = fields
                      ?.map((field, index) => ({ field, index }))
                      .filter(({ field }) => field.userType === UserType.INSTRUCTOR)

                    const surveyNotificationFields = instructorFields.filter(
                      ({ field }) => field.emailType !== 'INSTRUCTOR_EMAIL_RELEASED'
                    )
                    const releaseFields = instructorFields.filter(
                      ({ field }) => field.emailType === 'INSTRUCTOR_EMAIL_RELEASED'
                    )

                    return (
                      <>
                        {surveyNotificationFields.length > 0 && (
                          <MergedSurveyNotificationsCard fields={surveyNotificationFields} control={control} isReadOnly={isSlotClosed} isSlotPublishedOrLater={isSlotPublishedOrLater} showDevTools={showDevTools} slotId={slotId} />
                        )}
                        {surveyNotificationFields.length > 0 && releaseFields.length > 0 && (
                          <Divider sx={{ borderStyle: 'dashed', borderColor: 'grey.600', borderBottomWidth: 2, my: 4 }} />
                        )}
                        {releaseFields.map(({ field, index }) => (
                          <EmailSettingsCard key={field.id} isReadOnly={isSlotClosed} isSlotPublishedOrLater={isSlotPublishedOrLater} name={`settings.${index}`} setting={field} control={control} showDevTools={showDevTools} devTaskName={EMAIL_TYPE_TO_TASK[field.emailType]} slotId={slotId} />
                        ))}
                      </>
                    )
                  })()}
                </Stack>
              </TabPanel>
              <TabPanel value={tabOptions.ADMIN} sx={{ paddingTop: 4 }}>
                <Stack>
                  {(() => {
                    const adminFields = fields
                      ?.map((field, index) => ({ field, index }))
                      .filter(({ field }) => field.userType === UserType.ADMIN)

                    return adminFields.map(({ field, index }, i) => (
                      <React.Fragment key={field.id}>
                        {i > 0 && <Divider sx={{ borderStyle: 'dashed', borderColor: 'grey.600', borderBottomWidth: 2, my: 4 }} />}
                        <EmailSettingsCard isReadOnly={isSlotClosed} isSlotPublishedOrLater={isSlotPublishedOrLater} name={`settings.${index}`} setting={field} control={control} showDevTools={showDevTools} devTaskName={EMAIL_TYPE_TO_TASK[field.emailType]} slotId={slotId} />
                      </React.Fragment>
                    ))
                  })()}
                </Stack>
              </TabPanel>
            </TabContext>
          </form>
        </FormProvider>
      </GenericModal>
    </>
  )
}

export default SlotEmailSettingsFeature
